import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Checkbox, FormControlLabel, Grid, FormControl, Alert } from '@mui/material';
import { useTheme } from '@mui/material';
import ProductSelect from './ProductSelect';
import ProductCodeField from './ProductCodeField';

const loadNoCodeSettings = () => Promise.resolve({ productCode: null });

const AddRowPopup = ({ open, onClose, onSubmit, columns, editableFields, primaryField, tableMetadata, loadNextCode = loadNoCodeSettings }) => {
  const [formData, setFormData] = useState({});
  const ignoredFields = ['SYNC_TIME'];
  const [fieldWidths, setFieldWidths] = useState({});
  const [error, setError] = useState('');
  const [codeInfo, setCodeInfo] = useState(null);
  const [codeEntry, setCodeEntry] = useState({ useBarcode: false, number: '', barcode: '' });
  const codeSettings = codeInfo?.productCode || null;
  const theme = useTheme();

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setFormData({});
    setFieldWidths({});
    setError('');
    setCodeInfo(null);
    loadNextCode()
      .then((info) => {
        if (!active) return;
        setCodeInfo(info);
        setCodeEntry({ useBarcode: false, number: info.nextNumber ? String(info.nextNumber) : '', barcode: '' });
      })
      .catch((loadError) => {
        if (active) setError(loadError.message);
      });
    return () => { active = false; };
  }, [open, loadNextCode]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (type !== 'checkbox') {
      setFieldWidths(prevWidths => ({
        ...prevWidths,
        [name]: getFieldWidth(value)
      }));
    }
  };

  const handleAutocompleteChange = (name, newValue) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: newValue
    }));
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      if (!codeSettings) {
        await onSubmit(formData);
      } else if (codeEntry.useBarcode) {
        await onSubmit({ ...formData, [primaryField]: codeEntry.barcode });
      } else {
        // Drop anything typed in the plain code box before the settings loaded.
        const { [primaryField]: _typedCode, ...fields } = formData;
        // An untouched pre-filled number lets the server assign the next free one at save time.
        const edited = !codeSettings.lockNumber && codeEntry.number !== String(codeInfo.nextNumber);
        await onSubmit(fields, { codeNumber: edited ? codeEntry.number : undefined });
      }
      setFormData({});
      onClose();
    } catch (error) {
      setError(error.message);
    }
  };

  const getFieldWidth = (value) => {
    const baseWidth = 150; // Reduced minimum width
    const charWidth = 10; // Approximate width of a character
    return Math.min(150, Math.max(baseWidth, (value?.toString().length || 0) * charWidth));
  };

  // A prefix tenant gets ProductCodeField instead of the plain code box.
  const visibleColumns = columns.filter(column => !(codeSettings && column.field === primaryField));

  const nonBooleanFields = visibleColumns.filter(column => 
    (editableFields.includes(column.field) || column.field === primaryField) && column.type !== 'boolean' && !ignoredFields.includes(column.field)
  );

  const multilineTextFields = nonBooleanFields.filter(column => column.type === 'multilineText');
  const otherNonBooleanFields = nonBooleanFields.filter(column => column.type !== 'multilineText');

  const booleanFields = visibleColumns.filter(column => 
    (editableFields.includes(column.field) || column.field === primaryField) && column.type === 'boolean'
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      sx={{ 
        '& .MuiDialog-paper': { 
          width: '700px', 
          maxWidth: '950px',
          borderRadius: '20px', // Increased roundness
        } 
      }}
    >
      <DialogTitle sx={{ paddingTop: '20px' }}><strong>Add New Product</strong></DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          {codeSettings && (
            <ProductCodeField
              settings={codeSettings}
              nextCode={codeInfo.nextCode}
              entry={codeEntry}
              onChange={setCodeEntry}
            />
          )}
          <Grid container spacing={2} sx={{ mt: 0.05 }}>
            {otherNonBooleanFields.map(column => {
              const fieldWidth = fieldWidths[column.field] || getFieldWidth(formData[column.field]);
              return (
                <Grid item key={column.field} xs={12} sm="auto">
                  <div style={{ marginBottom: '16px' }}>
                    {['singleSelect', 'multipleSelects'].includes(column.type) ? (
                      <FormControl sx={{ width: `${fieldWidth}px`, minWidth: '150px' }}>
                        <ProductSelect
                          id={`${column.field}-combo-box`}
                          label={column.headerName}
                          options={column.options || []}
                          multiple={column.type === 'multipleSelects'}
                          value={formData[column.field]}
                          onChange={value => handleAutocompleteChange(column.field, value)}
                          required={column.field === primaryField}
                        />
                      </FormControl>
                    ) : (
                      <TextField
                        label={column.headerName}
                        name={column.field}
                        value={formData[column.field] || ''}
                        onChange={handleChange}
                        type={column.type === 'number' ? 'number' : 'text'}
                        required={column.field === primaryField}
                        sx={{ 
                          width: `${fieldWidth}px`, 
                          minWidth: '150px',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: error && column.field === primaryField ? 'red' : 'inherit',
                            },
                            '&:hover fieldset': {
                              borderColor: error && column.field === primaryField ? 'red' : 'inherit',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: error && column.field === primaryField ? 'red' : 'primary.main',
                            },
                          },
                        }}
                      />
                    )}
                  </div>
                </Grid>
              );
            })}
          </Grid>
          {multilineTextFields.map(column => (
            <TextField
              key={column.field}
              label={column.headerName}
              name={column.field}
              value={formData[column.field] || ''}
              onChange={handleChange}
              multiline
              minRows={2}
              maxRows={100}
              required={column.field === primaryField}
              sx={{
                width: '100%',
                minWidth: '300px',
                marginTop: '16px',
                marginBottom: '16px',
                '& .MuiInputBase-root': {
                  height: 'auto',
                  overflow: 'hidden',
                  resize: 'none'
                }
              }}
            />
          ))}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {booleanFields.map(column => (
              <Grid item key={column.field}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name={column.field}
                      checked={formData[column.field] || false}
                      onChange={handleChange}
                    />
                  }
                  label={column.headerName}
                />
              </Grid>
            ))}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, backgroundColor: '#ffebee', color: '#d32f2f' }}>
              {error}
            </Alert>
          )}
        </div>
        <div>
          <Button onClick={onClose} sx={{ marginRight: '10px' }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ marginLeft: '10px' }}>
            Add Product
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AddRowPopup;
